import React, { ReactNode } from 'react';
import moment from 'moment';

import {
  CHALLENGE_STATUS,
  ChallengeStatusType,
} from '../../../../constants/challengeStatus';
import { ChallengeImportantDatesInfo } from '../../../../interfaces/ChallengeInfo';
import { getFormattedDateInTz } from '../../../../../shared/utils/timezoneUtils/getFormattedDateInTz';
import { localDataFormat } from '../../../../../shared/constants/apiDateFormat';
import { getTimezoneLabel } from '../../../../../shared/utils/timezoneUtils/getTimezoneLabel';
import { getIsRegistrationPeriod } from '../../../../utils/getIsRegistrationPeriod/getIsRegistrationPeriod';

type GetChallengeTimerReturnType = {
  timerEpoch: number | null;
  description: ReactNode | null;
};

export const getChallengeTimerParams = (
  challengeStatus: ChallengeStatusType,
  importantDates: ChallengeImportantDatesInfo,
): GetChallengeTimerReturnType => {
  //All dates should be present in the valid challenge
  if (
    !importantDates.startDate ||
    !importantDates.endDate ||
    !importantDates.registrationStartDate ||
    !importantDates.registrationEndDate ||
    !importantDates.timeZone
  ) {
    return {
      timerEpoch: null,
      description: null,
    };
  }

  const startChallengeEpoch = moment(importantDates.startDate).unix();
  const endChallengeEpoch = moment(importantDates.endDate).unix();

  const registrationStartEpoch = moment(importantDates.registrationStartDate).unix();
  const registrationEndEpoch = moment(importantDates.registrationEndDate).unix();

  const timeZoneLabel = getTimezoneLabel(importantDates.timeZone);

  const isRegistrationPeriod = getIsRegistrationPeriod(
    importantDates.registrationStartDate,
    importantDates.registrationEndDate,
  );

  switch (challengeStatus) {
    case CHALLENGE_STATUS.ACTIVE:
      if (isRegistrationPeriod) {
        return {
          timerEpoch: registrationEndEpoch,
          description: (
            <>
              <p>The challenge has started.</p>
              <p>
                Registration is open until&nbsp;
                <span>
                  {getFormattedDateInTz(
                    importantDates.registrationEndDate,
                    importantDates.timeZone,
                    localDataFormat,
                  )}
                  &nbsp;({timeZoneLabel})
                </span>
              </p>
            </>
          ),
        };
      } else {
        return {
          timerEpoch: endChallengeEpoch,
          description: (
            <p>
              The Registration is closed, but challenge is active to&nbsp;
              <span>
                {getFormattedDateInTz(
                  importantDates.endDate,
                  importantDates.timeZone,
                  localDataFormat,
                )}
                &nbsp;({timeZoneLabel})
              </span>
            </p>
          ),
        };
      }
    case CHALLENGE_STATUS.PUBLISHED:
      if (isRegistrationPeriod) {
        return {
          timerEpoch: startChallengeEpoch,
          description: (
            <>
              <p>The challenge hasn&apos;t started. You can join the challenge now.</p>
              <p>
                The challenge starts on&nbsp;
                <span>
                  {getFormattedDateInTz(
                    importantDates.startDate,
                    importantDates.timeZone,
                    localDataFormat,
                  )}
                  &nbsp;({timeZoneLabel})
                </span>
              </p>
            </>
          ),
        };
      } else {
        return {
          timerEpoch: registrationStartEpoch,
          description: (
            <>
              <p>The challenge hasn&apos;t started yet.</p>
              <p>
                You can join starting&nbsp;
                <span>
                  {getFormattedDateInTz(
                    importantDates.registrationStartDate,
                    importantDates.timeZone,
                    localDataFormat,
                  )}
                  &nbsp;({timeZoneLabel})
                </span>
              </p>
            </>
          ),
        };
      }
    default:
    case CHALLENGE_STATUS.DRAFT:
    case CHALLENGE_STATUS.FINISHED:
      return {
        timerEpoch: null,
        description: null,
      };
  }
};
